import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  useToast,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Divider,
  Code,
  Tooltip
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, ViewIcon } from '@chakra-ui/icons';

interface PromptRule {
  id: number;
  name: string;
  description: string;
  prompt_template: string;
  is_active: number;
  priority: number;
  created_at: string;
  updated_at: string;
}

const PromptRulesPage: React.FC = () => {
  const [rules, setRules] = useState<PromptRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState<PromptRule | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt_template: '',
    is_active: 1,
    priority: 0
  });
  const [previewRule, setPreviewRule] = useState<PromptRule | null>(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deleteRuleId, setDeleteRuleId] = useState<number | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 프롬프트 규칙 조회
  const fetchRules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/graph/prompt-rules', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json; charset=utf-8'
        }
      });
      
      if (response.ok) {
        // UTF-8로 응답 받기 (이모지 완벽 지원)
        const text = await response.text();
        const data = JSON.parse(text);
        // 데이터가 배열인지 확인
        if (Array.isArray(data)) {
          setRules(data);
        } else if (data && Array.isArray(data.data)) {
          setRules(data.data);
        } else {
          console.warn('Unexpected prompt rules data format:', data);
          setRules([]);
        }
      } else {
        throw new Error('프롬프트 규칙 조회 실패');
      }
    } catch (error) {
      console.error('프롬프트 규칙 조회 오류:', error);
      toast({
        title: '오류',
        description: '프롬프트 규칙을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // 프롬프트 규칙 추가/수정
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = isEditing 
        ? `/graph/prompt-rules/${editingRule?.id}`
        : '/graph/prompt-rules';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      // JSON 문자열로 직접 전송 (브라우저가 자동으로 UTF-8 처리)
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // UTF-8로 응답 받기 (이모지 완벽 지원)
        const text = await response.text();
        const data = JSON.parse(text);
        toast({
          title: '성공',
          description: data.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        onClose();
        resetForm();
        fetchRules();
      } else {
        const errorText = await response.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || '저장 실패');
      }
    } catch (error) {
      console.error('프롬프트 규칙 저장 오류:', error);
      toast({
        title: '오류',
        description: error instanceof Error ? error.message : '저장 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 프롬프트 규칙 삭제
  const handleDelete = async () => {
    if (!deleteRuleId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/graph/prompt-rules/${deleteRuleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: '성공',
          description: data.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        onDeleteClose();
        fetchRules();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || '삭제 실패');
      }
    } catch (error) {
      console.error('프롬프트 규칙 삭제 오류:', error);
      toast({
        title: '오류',
        description: error instanceof Error ? error.message : '삭제 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 폼 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      prompt_template: '',
      is_active: 1,
      priority: 0
    });
    setEditingRule(null);
    setIsEditing(false);
  };

  // 편집 모드 열기
  const handleEdit = (rule: PromptRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      prompt_template: rule.prompt_template,
      is_active: rule.is_active,
      priority: rule.priority
    });
    setIsEditing(true);
    onOpen();
  };

  // 새 규칙 추가 모드 열기
  const handleAdd = () => {
    resetForm();
    setIsEditing(false);
    onOpen();
  };

  // 미리보기 열기
  const handlePreview = (rule: PromptRule) => {
    setPreviewRule(rule);
    onPreviewOpen();
  };

  // 삭제 확인 열기
  const handleDeleteClick = (ruleId: number) => {
    setDeleteRuleId(ruleId);
    onDeleteOpen();
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchRules();
  }, []);

  return (
    <Box p={6}>
      {/* 페이지 헤더 */}
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="lg" color="blue.700" mb={2}>
            프롬프트 규칙 관리
          </Heading>
          <Text color="gray.600">
            AI 응답 생성을 위한 프롬프트 규칙을 관리합니다.
          </Text>
        </Box>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={handleAdd}
        >
          새 규칙 추가
        </Button>
      </HStack>

      {/* 프롬프트 규칙 테이블 */}
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
        <Table variant="simple">
          <Thead bg={useColorModeValue('gray.50', 'gray.700')}>
            <Tr>
              <Th>이름</Th>
              <Th>설명</Th>
              <Th>우선순위</Th>
              <Th>상태</Th>
              <Th>생성일</Th>
              <Th>작업</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rules.map((rule) => (
              <Tr key={rule.id}>
                <Td fontWeight="medium">{rule.name}</Td>
                <Td>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {rule.description || '-'}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={rule.priority >= 80 ? 'red' : rule.priority >= 50 ? 'orange' : 'green'}>
                    {rule.priority}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={rule.is_active ? 'green' : 'gray'}>
                    {rule.is_active ? '활성' : '비활성'}
                  </Badge>
                </Td>
                <Td fontSize="sm" color="gray.600">
                  {new Date(rule.created_at).toLocaleDateString()}
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <Tooltip label="미리보기">
                      <IconButton
                        aria-label="미리보기"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePreview(rule)}
                      />
                    </Tooltip>
                    <Tooltip label="편집">
                      <IconButton
                        aria-label="편집"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(rule)}
                      />
                    </Tooltip>
                    <Tooltip label="삭제">
                      <IconButton
                        aria-label="삭제"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(rule.id)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* 추가/편집 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? '프롬프트 규칙 편집' : '새 프롬프트 규칙 추가'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>규칙 이름</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="예: 기본 답변 규칙"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>설명</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="규칙에 대한 설명을 입력하세요"
                    rows={2}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>프롬프트 템플릿</FormLabel>
                  <Textarea
                    value={formData.prompt_template}
                    onChange={(e) => setFormData({ ...formData, prompt_template: e.target.value })}
                    placeholder="프롬프트 템플릿을 입력하세요. {{query}}, {{context}} 등의 변수를 사용할 수 있습니다."
                    rows={8}
                    fontFamily="mono"
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    사용 가능한 변수: {`{{query}}`, `{{context}}`}
                  </Text>
                </FormControl>

                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel>우선순위</FormLabel>
                    <NumberInput
                      value={formData.priority}
                      onChange={(valueString, valueNumber) => 
                        setFormData({ ...formData, priority: valueNumber })
                      }
                      min={0}
                      max={100}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>활성화</FormLabel>
                    <Switch
                      isChecked={formData.is_active === 1}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                    />
                  </FormControl>
                </HStack>

                <HStack spacing={4} w="full" justify="flex-end">
                  <Button variant="ghost" onClick={onClose}>
                    취소
                  </Button>
                  <Button type="submit" colorScheme="blue">
                    {isEditing ? '수정' : '추가'}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 미리보기 모달 */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>프롬프트 규칙 미리보기</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {previewRule && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={2}>규칙 이름</Text>
                  <Text>{previewRule.name}</Text>
                </Box>
                
                {previewRule.description && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>설명</Text>
                    <Text>{previewRule.description}</Text>
                  </Box>
                )}
                
                <Box>
                  <Text fontWeight="bold" mb={2}>프롬프트 템플릿</Text>
                  <Code p={4} borderRadius="md" w="full" whiteSpace="pre-wrap">
                    {previewRule.prompt_template}
                  </Code>
                </Box>
                
                <HStack>
                  <Badge colorScheme={previewRule.is_active ? 'green' : 'gray'}>
                    {previewRule.is_active ? '활성' : '비활성'}
                  </Badge>
                  <Badge colorScheme={previewRule.priority >= 80 ? 'red' : previewRule.priority >= 50 ? 'orange' : 'green'}>
                    우선순위: {previewRule.priority}
                  </Badge>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              프롬프트 규칙 삭제
            </AlertDialogHeader>
            <AlertDialogBody>
              정말로 이 프롬프트 규칙을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PromptRulesPage;
